<template>
  <main class="plan-page">
    <!-- Hero Section -->
    <section class="hero">
      <div class="container">
        <h1>The Plan</h1>
        <p class="subtitle">A 5 step plan to solve a county-wide problem</p>
      </div>
    </section>

    <!-- Story Section -->
    <section class="story-section">
      <div class="container">
        <div class="story-content">
          <h2>How it started</h2>
          <p>
            When I first started this project, it was because I had a few old laptops not being used and asked a teacher if any of their students needed them.
            They told me of the 75 students graduating up to middle school, around <strong>50-60% would probably not have access to a computer at home</strong>.
          </p>
          <p>
            So I set a personal goal to get 50 laptops by May of 2026 for these students before they started middle school. The plan was to just ask coworkers,
            friends, and family to get donations of old laptops. I thought I could help this class, but that the larger problem at the
            county level was out of my hands.
          </p>
        </div>
      </div>
    </section>

    <!-- Discovery Section -->
    <section class="discovery-section">
      <div class="container">
        <div class="discovery-content">
          <h2>Finding the source</h2>
          <p>
            I received a handful of laptops, and a small amount of money to source used laptops on eBay. I noticed that a lot of Chromebooks from other
            school districts were being sold off in lots of 5-10 for $20-30 each. I bought a few of these lots, and refurbished them to be given to students
            at the school. But I knew that sellers on eBay do not work for free, and if they are selling these laptops for $20, there is a large supply of
            very cheap affordable laptops out there.
          </p>
          <p>
            I started asking around and talking to people about where our school classroom laptops end up after they were decommissioned, and I learned about
            <strong>GovDeals.com</strong> which is a government surplus auction site. I found that school districts all over the country were selling off
            their used classroom laptops by the pallet.
          </p>
        </div>
      </div>
    </section>

    <!-- Images Section -->
    <section class="images-section">
      <div class="container">
        <div class="image-showcase">
          <div class="image-card">
            <img src="/assets/img/govdeals/556_chromebooks.webp" alt="566 Chromebooks from Georgia School District" />
          </div>
          <div class="image-card">
            <img src="/assets/img/govdeals/556_chromebooks2.webp" alt="Close-up of surplus Chromebooks" />
          </div>
          <div class="image-card">
            <img src="/assets/img/govdeals/pallets.webp" alt="Pallets of surplus laptops" />
          </div>
        </div>

        <div class="breakthrough">
          <h3>The Solution</h3>
          <p class="highlight-text">
            The going rate for these lots is around $5 per laptop when bought in bulk (500+). At this pricepoint the goal of ensuring every student in the county has access to technology at home starts to feel achievable.
          </p>
        </div>
      </div>
    </section>

    <!-- The Plan Section -->
    <section class="plan-section">
      <div class="container">
        <h2>So This is the Plan</h2>
        <div class="plan-steps">
          <div class="step completed">
            <div class="step-icon">
              <i class="icon">✓</i>
            </div>
            <div class="step-content">
              <h3>Build a Platform</h3>
              <p>Created a public website and teacher portal to manage inventory and empower teachers to coordinate the needs of their students</p>
              <span class="status-badge completed">Completed</span>
            </div>
          </div>

          <div class="step">
            <div class="step-icon">
              <i class="icon">2</i>
            </div>
            <div class="step-content">
              <h3>Raise Funds</h3>
              <p>Secure funding to purchase a wholesale lot of decommissioned laptops (Including power supplies and shipping costs)</p>
              <span class="status-badge">In Progress</span>
              <div class="step-actions">
                <givebutter-widget id="pXylnj"></givebutter-widget>
                <router-link to="/donate?section=money" class="step-button donate-button">Donate</router-link>
              </div>
            </div>
          </div>

          <div class="step">
            <div class="step-icon">
              <i class="icon">3</i>
            </div>
            <div class="step-content">
              <h3>Purchase Wholesale Lot</h3>
              <p>Buy decommissioned laptops from school districts at wholesale prices</p>
              <span class="status-badge">Upcoming</span>
            </div>
          </div>

          <div class="step">
            <div class="step-icon">
              <i class="icon">4</i>
            </div>
            <div class="step-content">
              <h3>Host Refurbishment Event</h3>
              <p>Weekend-long laptop refurbishment event with volunteers to prepare devices for students</p>
              <span class="status-badge">Upcoming</span>
              <div class="step-actions">
                <router-link to="/volunteer" class="step-button volunteer-button">Volunteer</router-link>
              </div>
            </div>
          </div>

          <div class="step">
            <div class="step-icon">
              <i class="icon">5</i>
            </div>
            <div class="step-content">
              <h3>Distribute to Students</h3>
              <p>Get laptops into the hands of students in need through their schools</p>
              <span class="status-badge">Upcoming</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Ongoing Initiatives Section -->
    <section class="ongoing-section">
      <div class="container">
        <h2>What We'll Continue Doing</h2>
        <p class="section-intro">While we work toward our 5-step plan, we're not stopping our current efforts to help students.</p>

        <div class="initiatives-grid">
          <div class="initiative-card">
            <div class="initiative-icon">💻</div>
            <h3>Accepting Donated Laptops</h3>
            <p>We continue to accept and refurbish donated laptops from individuals and businesses. Every laptop donated is one more student with access to technology.</p>
            <router-link to="/donate?section=equipment" class="initiative-link">Learn More →</router-link>
          </div>

          <div class="initiative-card">
            <div class="initiative-icon">🏫</div>
            <h3>Working with Teachers</h3>
            <p>We partner with teachers throughout Brevard County to identify students in need and ensure laptops get into the right hands through trusted school channels.</p>
            <router-link to="/contact" class="initiative-link">Get Involved →</router-link>
          </div>

          <div class="initiative-card">
            <div class="initiative-icon">🤝</div>
            <h3>Engaging Officials</h3>
            <p>We'll be working with county and state officials to create a direct pipeline for decommissioned school laptops, cutting out the middleman and getting devices to students who need them.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta-section">
      <div class="container">
        <h2>Join Us in Making This Happen</h2>
        <p>Whether you can donate, volunteer, or spread the word—every bit helps us reach more students.</p>
        <div class="cta-buttons">
          <router-link to="/donate" class="btn btn-primary">Support Our Mission</router-link>
          <router-link to="/volunteer" class="btn btn-secondary">Volunteer</router-link>
          <router-link to="/contact" class="btn btn-outline">Get in Touch</router-link>
        </div>
      </div>
    </section>
  </main>
</template>

<script setup lang="ts">
// No special logic needed for this static page
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&family=Inter:wght@400;500;600&display=swap');

.plan-page {
  font-family: 'Inter', sans-serif;
}

/* Hero Section */
.hero {
  background: linear-gradient(135deg, #2c4a6b 0%, #3ba3d1 100%);
  color: white;
  padding: 4rem 0;
  text-align: center;
}

.hero h1 {
  font-family: 'Poppins', sans-serif;
  font-size: 3.5rem;
  font-weight: 800;
  margin: 0 0 1rem 0;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
}

.hero .subtitle {
  font-size: 1.5rem;
  font-weight: 400;
  margin: 0;
  opacity: 0.95;
}

/* Story Section */
.story-section {
  padding: 4rem 0;
  background-color: #ffffff;
}

.story-content h2 {
  font-family: 'Poppins', sans-serif;
  font-size: 2.5rem;
  color: #2c4a6b;
  margin-bottom: 1.5rem;
  text-align: center;
}

.story-content p {
  font-size: 1.125rem;
  line-height: 1.8;
  color: #333;
  margin-bottom: 1.5rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
}

/* Discovery Section */
.discovery-section {
  padding: 4rem 0;
  background-color: #f5f7fa;
}

.discovery-content h2 {
  font-family: 'Poppins', sans-serif;
  font-size: 2.5rem;
  color: #2c4a6b;
  margin-bottom: 1.5rem;
  text-align: center;
}

.discovery-content p {
  font-size: 1.125rem;
  line-height: 1.8;
  color: #333;
  margin-bottom: 1.5rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
}

/* Images Section */
.images-section {
  padding: 4rem 0;
  background-color: #ffffff;
}

.image-showcase {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
}

.image-card {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  background: white;
}

.image-card img {
  width: 100%;
  height: auto;
  display: block;
}

.breakthrough {
  text-align: center;
  margin-top: 3rem;
  padding: 2rem;
  background: linear-gradient(135deg, rgba(44, 74, 107, 0.05) 0%, rgba(59, 163, 209, 0.05) 100%);
  border-radius: 8px;
}

.breakthrough h3 {
  font-family: 'Poppins', sans-serif;
  font-size: 2rem;
  color: #2c4a6b;
  margin-bottom: 1rem;
}

.highlight-text {
  font-size: 1.25rem;
  line-height: 1.8;
  color: #333;
  max-width: 700px;
  margin: 0 auto;
}

/* Plan Section */
.plan-section {
  padding: 4rem 0;
  background: linear-gradient(135deg, #2c4a6b 0%, #3ba3d1 100%);
  color: white;
}

.plan-section h2 {
  font-family: 'Poppins', sans-serif;
  font-size: 2.5rem;
  text-align: center;
  margin-bottom: 3rem;
}

.plan-steps {
  max-width: 900px;
  margin: 0 auto;
}

.step {
  display: flex;
  gap: 2rem;
  margin-bottom: 2.5rem;
  background: rgba(255, 255, 255, 0.1);
  padding: 2rem;
  border-radius: 12px;
  backdrop-filter: blur(10px);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.step:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
}

.step.completed {
  background: rgba(255, 255, 255, 0.15);
}

.step-icon {
  flex-shrink: 0;
  width: 60px;
  height: 60px;
  background: white;
  color: #2c4a6b;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: bold;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.step.completed .step-icon {
  background: #4caf50;
  color: white;
}

.step-content {
  flex: 1;
}

.step-content h3 {
  font-family: 'Poppins', sans-serif;
  font-size: 1.5rem;
  margin: 0 0 0.5rem 0;
}

.step-content p {
  margin: 0 0 1rem 0;
  opacity: 0.95;
  line-height: 1.6;
}

.status-badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 500;
}

.status-badge.completed {
  background: #4caf50;
  color: white;
}

.step-actions {
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-items: flex-start;
}

.step-button {
  display: inline-block;
  padding: 0.75rem 1.5rem;
  background: white;
  color: #2c4a6b;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.95rem;
  border-radius: 5px;
  border: 2px solid white;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.step-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  background: #f5f5f5;
}

.donate-button {
  background: #e94f3a;
  color: white;
  border-color: #e94f3a;
  border-radius: 25px;
  padding: 0.75rem 2rem;
}

.donate-button:hover {
  background: #d43d2a;
  border-color: #d43d2a;
  box-shadow: 0 4px 12px rgba(233, 79, 58, 0.3);
}

.volunteer-button {
  background: #e94f3a;
  color: white;
  border-color: #e94f3a;
  border-radius: 25px;
  padding: 0.75rem 2rem;
}

.volunteer-button:hover {
  background: #d43d2a;
  border-color: #d43d2a;
  box-shadow: 0 4px 12px rgba(233, 79, 58, 0.3);
}

/* givebutter-widget {
  width: 100%;
  max-width: 400px;
} */

/* Sustainability Section */
.sustainability-section {
  padding: 4rem 0;
  background-color: #f5f7fa;
}

.sustainability-section h2 {
  font-family: 'Poppins', sans-serif;
  font-size: 2.5rem;
  color: #2c4a6b;
  text-align: center;
  margin-bottom: 2rem;
}

.sustainability-content {
  max-width: 800px;
  margin: 0 auto;
}

.sustainability-content > p {
  font-size: 1.125rem;
  line-height: 1.8;
  color: #333;
  margin-bottom: 2rem;
  text-align: center;
}

.vision-box {
  background: white;
  padding: 2rem;
  border-radius: 8px;
  border-left: 4px solid #3ba3d1;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.vision-box h3 {
  font-family: 'Poppins', sans-serif;
  font-size: 1.75rem;
  color: #2c4a6b;
  margin: 0 0 1rem 0;
}

.vision-box p {
  font-size: 1.125rem;
  line-height: 1.8;
  color: #333;
  margin: 0 0 1rem 0;
}

.vision-box p:last-child {
  margin-bottom: 0;
}

.choice-text {
  font-style: italic;
  color: #555;
  margin-top: 1.5rem;
}

/* Ongoing Initiatives Section */
.ongoing-section {
  padding: 4rem 0;
  background-color: #f5f7fa;
}

.ongoing-section h2 {
  font-family: 'Poppins', sans-serif;
  font-size: 2.5rem;
  color: #2c4a6b;
  text-align: center;
  margin-bottom: 1rem;
}

.section-intro {
  text-align: center;
  font-size: 1.125rem;
  color: #666;
  margin-bottom: 3rem;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
}

.initiatives-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  max-width: 1100px;
  margin: 0 auto;
}

.initiative-card {
  background: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  display: flex;
  flex-direction: column;
}

.initiative-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

.initiative-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.initiative-card h3 {
  font-family: 'Poppins', sans-serif;
  font-size: 1.5rem;
  color: #2c4a6b;
  margin: 0 0 1rem 0;
}

.initiative-card p {
  font-size: 1rem;
  line-height: 1.6;
  color: #666;
  margin: 0 0 1.5rem 0;
  flex-grow: 1;
}

.initiative-link {
  color: #3ba3d1;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.95rem;
  transition: color 0.3s ease;
  align-self: flex-start;
}

.initiative-link:hover {
  color: #2c4a6b;
}

/* CTA Section */
.cta-section {
  padding: 4rem 0;
  background: linear-gradient(135deg, #e94f3a 0%, #d43d2a 100%);
  color: white;
  text-align: center;
}

.cta-section h2 {
  font-family: 'Poppins', sans-serif;
  font-size: 2.5rem;
  margin-bottom: 1rem;
}

.cta-section > div > p {
  font-size: 1.25rem;
  margin-bottom: 2rem;
  opacity: 0.95;
}

.cta-buttons {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

.btn {
  display: inline-block;
  padding: 1rem 2rem;
  border-radius: 6px;
  text-decoration: none;
  font-weight: 600;
  font-size: 1.125rem;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.btn-primary {
  background-color: white;
  color: #e94f3a;
}

.btn-primary:hover {
  background-color: #f5f5f5;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.btn-secondary {
  background-color: #2c4a6b;
  color: white;
}

.btn-secondary:hover {
  background-color: #1e3249;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.btn-outline {
  background-color: transparent;
  color: white;
  border: 2px solid white;
}

.btn-outline:hover {
  background-color: white;
  color: #e94f3a;
  transform: translateY(-2px);
}

/* Responsive Design */
@media (max-width: 768px) {
  .hero h1 {
    font-size: 2.5rem;
  }

  .hero .subtitle {
    font-size: 1.125rem;
  }

  .story-content h2,
  .discovery-content h2,
  .plan-section h2,
  .sustainability-section h2,
  .cta-section h2 {
    font-size: 2rem;
  }

  .story-content p,
  .discovery-content p,
  .highlight-text,
  .sustainability-content > p,
  .vision-box p {
    font-size: 1rem;
  }

  .step {
    flex-direction: column;
    gap: 1rem;
  }

  .step-icon {
    width: 50px;
    height: 50px;
    font-size: 1.25rem;
  }

  .image-showcase {
    grid-template-columns: 1fr;
  }

  .cta-buttons {
    flex-direction: column;
    align-items: center;
  }

  .btn {
    width: 100%;
    max-width: 300px;
  }
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
}
</style>
